import { useMutation } from '@tanstack/react-query'
import type {
  AssistantRunRequest,
  AssistantStreamEvent,
  AssistantToolCallView,
  AssistantTurnView,
} from '@fieldops/shared'
import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { runAssistant } from '../../../lib/assistant-stream.js'
import { errorMessage, useTRPC } from '../../../lib/trpc.js'

/** A text the assistant drafted that leaves from the phone rather than the server (decision 86). */
export interface PhoneText {
  to: string | null
  body: string
  jobId: string | null
}

/**
 * One conversation with the assistant, as the job's Assistant tab and Eco
 * both hold it: the turns, the words arriving, the cards, and what Approve,
 * Reject and Undo do to them. Decisions 94 and 116.
 *
 * What differs is passed in. `refresh` is what the assistant may have
 * changed — a job's tab refetches the job, Eco the whole office — and runs
 * after every exchange and every Undo. `prepare` adds what goes with every
 * request (Eco's `focus`). `onSaved` hears that the server has stored the
 * exchange; `onToolCall` and `onFailure` are for the frog. How a conversation
 * starts and what Send does stay with the caller, because a job's opens with
 * a summary and Eco's opens on the first message.
 */
export function useConversation({
  sessionId,
  saved,
  refresh,
  silence,
  onPhoneText,
  prepare,
  onSaved,
  onToolCall,
  onFailure,
}: {
  sessionId: string | null
  /** The stored conversation. It replaces what the stream drew, except mid-run. */
  saved: readonly AssistantTurnView[] | undefined
  refresh: () => void
  /** Said when the stream fails before the server can say why. */
  silence: string
  onPhoneText: (text: PhoneText) => void
  prepare?: (request: AssistantRunRequest) => AssistantRunRequest
  onSaved?: () => void
  onToolCall?: (call: AssistantToolCallView) => void
  onFailure?: () => void
}) {
  const trpc = useTRPC()
  const [turns, setTurns] = useState<AssistantTurnView[]>([])
  const [streaming, setStreaming] = useState('')
  const [running, setRunning] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  // Stop was pressed on the last run; the server's own word on it follows.
  const [stopped, setStopped] = useState(false)
  const abort = useRef<AbortController | null>(null)
  const scroller = useRef<HTMLDivElement>(null)
  // Whether the person is reading the newest words. Scrolled up to re-read,
  // they are left where they are and offered a way back down.
  const pinned = useRef(true)
  const [atBottom, setAtBottom] = useState(true)

  // A card that changed — from the stream, an Approve or an Undo — goes back
  // in its own turn, or the last one when the stream has not named it yet.
  const replaceCall = (call: AssistantToolCallView) =>
    setTurns((current) => {
      const index = current.findIndex((turn) => turn.id === call.turnId)
      const at = index === -1 ? current.length - 1 : index
      if (at < 0) return current
      return current.map((turn, position) => {
        if (position !== at) return turn
        const exists = turn.toolCalls.some((row) => row.id === call.id)
        return {
          ...turn,
          toolCalls: exists ? turn.toolCalls.map((row) => (row.id === call.id ? call : row)) : [...turn.toolCalls, call],
        }
      })
    })

  const undoCall = useMutation(
    trpc.assistant.undo.mutationOptions({
      onSuccess: (call) => {
        replaceCall(call)
        refresh()
      },
      onError: (error) => setProblem(errorMessage(error)),
    }),
  )

  const onEvent = (event: AssistantStreamEvent) => {
    switch (event.type) {
      case 'turn':
        setTurns((current) =>
          current.some((turn) => turn.id === event.turn.id)
            ? current.map((turn) => (turn.id === event.turn.id ? event.turn : turn))
            : [...current, event.turn],
        )
        if (event.turn.role === 'ASSISTANT') setStreaming('')
        break
      case 'text':
        setStreaming((current) => current + event.delta)
        break
      case 'toolCall':
        replaceCall(event.toolCall)
        onToolCall?.(event.toolCall)
        break
      case 'done':
        setTurns(event.session.turns)
        onSaved?.()
        break
      case 'error':
        setProblem(event.message)
        onFailure?.()
        break
    }
  }

  const run = async (request: AssistantRunRequest) => {
    abort.current?.abort()
    const controller = new AbortController()
    abort.current = controller
    // Something new was asked: follow it down.
    pinned.current = true
    setAtBottom(true)
    setRunning(true)
    setProblem(null)
    setStopped(false)
    setStreaming('')
    try {
      await runAssistant(prepare ? prepare(request) : request, onEvent, controller.signal)
    } catch (error) {
      if (!controller.signal.aborted) {
        setProblem(error instanceof Error ? error.message : silence)
        onFailure?.()
      }
    } finally {
      if (abort.current === controller) {
        setRunning(false)
        setStreaming('')
        refresh()
      }
    }
  }

  const approve = (call: AssistantToolCallView, edits?: Record<string, unknown>) => {
    if (!sessionId) return
    // A text with no server to send it opens the phone's own window.
    if (call.card?.sms && call.card.sms.via !== 'server') {
      onPhoneText({
        to: (edits?.['to'] as string | undefined) ?? call.card.sms.to,
        body: (edits?.['body'] as string | undefined) ?? call.card.sms.body,
        jobId: call.card.job?.id ?? null,
      })
    }
    void run({ sessionId, action: 'approve', toolCallId: call.id, edits })
  }

  const reject = (call: AssistantToolCallView, reason?: string, resume?: boolean) => {
    if (!sessionId) return
    void run({ sessionId, action: 'reject', toolCallId: call.id, reason, resume })
  }

  // The saved conversation replaces what the stream drew, except mid-run.
  useEffect(() => {
    if (saved && !running) setTurns([...saved])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saved])

  useEffect(() => () => abort.current?.abort(), [])

  useEffect(() => {
    const node = scroller.current
    if (node && pinned.current) node.scrollTop = node.scrollHeight
  }, [turns, streaming, running])

  const onScroll = () => {
    const node = scroller.current
    if (!node) return
    const near = node.scrollHeight - node.scrollTop - node.clientHeight < 48
    pinned.current = near
    setAtBottom(near)
  }

  const jumpToLatest = () => {
    const node = scroller.current
    if (!node) return
    pinned.current = true
    setAtBottom(true)
    node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' })
  }

  return {
    turns,
    setTurns,
    streaming,
    running,
    problem,
    setProblem,
    /** The log element, kept scrolled to the newest words while the person is there. */
    scroller,
    onScroll,
    atBottom,
    jumpToLatest,
    /**
     * Stop: the stream closes, and the server finishes the step it is on and
     * stops. What ran stays done; a card already proposed still waits.
     */
    stop: () => {
      if (!abort.current) return
      setStopped(true)
      abort.current.abort()
    },
    stopped,
    run,
    approve,
    reject,
    undo: (call: AssistantToolCallView) => undoCall.mutate({ toolCallId: call.id }),
    undoing: (call: AssistantToolCallView) => undoCall.isPending && undoCall.variables?.toolCallId === call.id,
  }
}

/** Enter sends; Shift+Enter is a new line; an input method mid-word keeps its Enter. */
export function sendOnEnter(send: () => void) {
  return (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      send()
    }
  }
}
